import { notify, useAccess, useSession, useMySchedule, useUndoableRemove } from '@church/data'
import {
  BLOCKOUT_REPEAT_LABELS,
  BLOCKOUT_REPEATS,
  planTimeLabel,
  type BlockoutRepeat,
} from '@church/domain'
import {
  faBookOpen,
  faCalendarXmark,
  faCheck,
  faChevronRight,
  faPlus,
  faTrashCan,
  faXmark,
} from '@church/ui-icons'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useId, useState } from 'react'
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  ErrorState,
  Field,
  Icon,
  IconButton,
  Input,
  ListHeader,
  listRow,
  ListTitle,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'
import { DeclinePanel } from '../components/services/DeclinePanel.tsx'

/**
 * My Schedule: answer requests in one tap, and say when you are away. Saying
 * no, adding dates away and removing them happen in panels.
 */
export const Route = createFileRoute('/_authed/_shell/my-schedule')({
  component: MyScheduleScreen,
})

const prettyDate = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

function MyScheduleScreen() {
  const mine = useMySchedule()
  const navigate = useNavigate()
  const removeLater = useUndoableRemove()
  const access = useAccess()
  const email = useSession().me?.user.email
  // The request being declined, and the blockout open in its panel; each
  // `shown` outlives closing so the panel can slide away with its contents.
  const [declining, setDeclining] = useState<string | null>(null)
  const [decliningShown, setDecliningShown] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [blockoutId, setBlockoutId] = useState<string | null>(null)
  const [blockoutShown, setBlockoutShown] = useState<string | null>(null)

  if (mine.isLoading) return <Spinner />
  if (mine.error && mine.waiting.length + mine.upcoming.length === 0)
    return (
      <ErrorState
        error={mine.error}
        safe="Your answers and dates away are safe on the server."
        onRetry={() => void mine.refetch()}
      />
    )
  if (!mine.linked) {
    return (
      <div className="space-y-6">
        <PageHeader title="My schedule" />
        <EmptyState
          title="Your login isn’t linked to a directory record yet"
          hint={
            access.has('people', 'editor')
              ? `Requests to serve go to a person in the directory. Find yours in People; the account ${email ?? 'you signed in with'} needs linking to it.`
              : `Requests to serve go to a person in the directory. Ask the church office to link ${email ?? 'your account'} to your record; then your requests show here.`
          }
          action={
            access.has('people', 'editor') ? (
              <Link
                to="/people"
                className="inline-flex min-h-11 items-center rounded-control bg-brand-solid px-button-x text-sm font-medium text-brand-on-solid hover:bg-brand-solid-hover"
              >
                Open People
              </Link>
            ) : undefined
          }
        />
      </div>
    )
  }
  const request = mine.waiting.find((r) => r.id === decliningShown)
  const blockout = mine.blockouts.find((b) => b.id === blockoutShown)
  // The request whose answer was refused: its card says so, with Retry.
  const failedId = mine.respond.isError ? mine.respond.variables?.id : undefined

  const accept = (r: (typeof mine.waiting)[number]) =>
    mine.respond.mutate(
      { id: r.id, accept: true },
      {
        onSuccess: () =>
          notify({
            title: `You’re serving as ${r.positionName}`,
            description: `${prettyDate(r.date)} at ${r.startTime}. The scheduler can see you said yes.`,
            action: {
              label: 'Open Music Stand',
              run: () => void navigate({ to: '/stand/$planId', params: { planId: r.planId } }),
            },
          }),
      },
    )

  return (
    <div className="space-y-6">
      <PageHeader title="My schedule" />
      <ErrorNote error={mine.addBlockout.error ?? (failedId ? null : mine.respond.error)} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card bleed className="h-fit lg:col-span-2">
          <ListHeader>Waiting for your answer</ListHeader>
          {mine.waiting.length === 0 && (
            <p className="px-4 py-4 text-sm text-content-muted">Nothing waiting.</p>
          )}
          <ul className="divide-y divide-border-subtle">
            {mine.waiting.map((r) => (
              <li key={r.id} className="space-y-3 px-4 py-4">
                <div>
                  <p className="text-base font-semibold text-content-default">
                    {prettyDate(r.date)}
                    <span className="ml-2 font-normal tabular-nums text-content-muted">
                      {r.startTime}
                    </span>
                  </p>
                  <p className="text-sm text-content-muted">
                    {r.positionName} · {r.teamName} · {r.serviceTypeName}
                  </p>
                  {timesText(r) && (
                    <p className="text-sm text-content-muted tabular-nums">{timesText(r)}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="success"
                    icon={faCheck}
                    className="min-h-12 flex-1 text-base sm:flex-none sm:px-6"
                    pending={mine.respond.isPending && mine.respond.variables?.id === r.id}
                    pendingLabel="Accepting…"
                    onClick={() => accept(r)}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="danger"
                    icon={faXmark}
                    className="min-h-12 flex-1 text-base sm:flex-none sm:px-6"
                    onClick={() => {
                      mine.respond.reset()
                      setDecliningShown(r.id)
                      setDeclining(r.id)
                    }}
                  >
                    Reject
                  </Button>
                </div>
                {failedId === r.id && declining === null && (
                  <ErrorNote
                    error={mine.respond.error}
                    onRetry={() =>
                      mine.respond.variables && mine.respond.mutate(mine.respond.variables)
                    }
                    safe="Your answer wasn’t sent: this request is still waiting for you."
                  />
                )}
              </li>
            ))}
          </ul>
          <ListHeader>You’re serving</ListHeader>
          {mine.upcoming.length === 0 && (
            <p className="px-4 py-4 text-sm text-content-muted">Nothing coming up.</p>
          )}
          <ul className="divide-y divide-border-subtle">
            {mine.upcoming.map((r) => (
              <li key={r.id} className={`${listRow} flex items-center justify-between gap-3`}>
                <span className="min-w-0">
                  <span className="block text-base font-medium text-content-default">
                    {prettyDate(r.date)}
                    <span className="ml-2 font-normal tabular-nums text-content-muted">
                      {r.startTime}
                    </span>
                  </span>
                  <span className="block text-sm text-content-muted">
                    {r.positionName} · {r.teamName}
                  </span>
                  {timesText(r) && (
                    <span className="block text-sm text-content-muted tabular-nums">
                      {timesText(r)}
                    </span>
                  )}
                </span>
                <Link
                  to="/stand/$planId"
                  params={{ planId: r.planId }}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-content-brand no-underline hover:bg-surface-sunken"
                >
                  <Icon icon={faBookOpen} />
                  Music Stand
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        <Card bleed className="h-fit">
          <ListTitle title="Dates I’m away">
            <IconButton icon={faPlus} label="Add dates away" onClick={() => setAdding(true)} />
          </ListTitle>
          {mine.blockouts.length === 0 && (
            <p className="px-4 py-4 text-sm text-content-muted">
              None. Add the dates you can’t serve, and nobody will ask you for them.
            </p>
          )}
          <ul className="divide-y divide-border-subtle">
            {mine.blockouts.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => {
                    setBlockoutShown(b.id)
                    setBlockoutId(b.id)
                  }}
                  className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                >
                  <Icon icon={faCalendarXmark} className="size-4 text-content-subtle" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-medium text-content-default">
                      {rangeText(b.startsOn, b.endsOn)}
                    </span>
                    {(b.repeats || b.reason) && (
                      <span className="block truncate text-sm text-content-muted">
                        {[b.repeats, b.reason].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <Icon icon={faChevronRight} className="size-3.5 text-content-subtle" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <DeclinePanel
        open={declining !== null && request !== undefined}
        onOpenChange={(next) => {
          if (!next) setDeclining(null)
        }}
        description={request ? `${prettyDate(request.date)} · ${request.positionName}` : undefined}
        pending={mine.respond.isPending}
        error={declining !== null ? mine.respond.error : null}
        onDecline={(reason, done) => {
          if (!request) return
          mine.respond.mutate(
            { id: request.id, accept: false, reason },
            {
              onSuccess: () => {
                done()
                setDeclining(null)
                notify({
                  title: `Rejected ${request.positionName}, ${prettyDate(request.date)}`,
                  description: `The scheduler sees your reason: “${reason}”.`,
                })
              },
            },
          )
        }}
      />

      <AddBlockoutPanel open={adding} onClose={() => setAdding(false)} />

      <SidePanel
        open={blockoutId !== null && blockout !== undefined}
        onOpenChange={(next) => {
          if (!next) setBlockoutId(null)
        }}
        title={blockout ? rangeText(blockout.startsOn, blockout.endsOn) : ''}
        description="Nobody asks you to serve on these dates."
        footer={
          blockout ? (
            <Button
              variant="quietDanger"
              icon={faTrashCan}
              className="-ml-4"
              onClick={() => {
                setBlockoutId(null)
                removeLater({
                  id: blockout.id,
                  title: `Removed your dates away: ${rangeText(blockout.startsOn, blockout.endsOn)}`,
                  description: 'You can be asked to serve on them again.',
                  commit: () => mine.removeBlockout.mutateAsync(blockout.id),
                })
              }}
            >
              Remove these dates
            </Button>
          ) : undefined
        }
      >
        {blockout?.repeats && (
          <PanelSection title="Repeats">
            <p className="text-sm text-content-default">{blockout.repeats}</p>
          </PanelSection>
        )}
        {blockout && (
          <PanelSection title="Why">
            <p className="text-sm text-content-default">{blockout.reason ?? 'No reason given.'}</p>
          </PanelSection>
        )}
      </SidePanel>
    </div>
  )
}

/**
 * The times someone serves on a plan when there is more than one:
 * "Rehearsal 19:30 (Thu 11 Mar) · 9:00". Null for a plan with one time.
 */
function timesText(request: {
  date: string
  times: { kind: string; name: string | null; date: string; startTime: string }[]
}) {
  if (request.times.length <= 1) return null
  return request.times
    .map((time) =>
      time.date === request.date
        ? planTimeLabel(time)
        : `${planTimeLabel(time)} (${new Date(`${time.date}T12:00:00Z`).toLocaleDateString(
            undefined,
            { weekday: 'short', day: 'numeric', month: 'short' },
          )})`,
    )
    .join(' · ')
}

/** "Sun, Oct 25" or "Sun, Oct 25 – Sat, Oct 31". */
function rangeText(from: string, to: string) {
  const day = (date: string) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  return to === from ? day(from) : `${day(from)} – ${day(to)}`
}

/** Dates away: one day, or a range, and why if you like. */
function AddBlockoutPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const mine = useMySchedule()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [why, setWhy] = useState('')
  const [repeat, setRepeat] = useState<BlockoutRepeat | ''>('')
  const [until, setUntil] = useState('')
  const formId = useId()

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Dates I’m away"
      description="Nobody will ask you to serve on them."
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faCalendarXmark}
          className="py-1.5"
          disabled={!from}
          pending={mine.addBlockout.isPending}
          pendingLabel="Blocking out…"
        >
          Block out
        </Button>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          mine.addBlockout.mutate(
            {
              startsOn: from,
              endsOn: to || from,
              reason: why || null,
              rule: repeat ? { ...BLOCKOUT_REPEATS[repeat], ...(until && { until }) } : null,
            },
            {
              onSuccess: () => {
                notify({
                  title: `Blocked out ${rangeText(from, to || from)}`,
                  description: repeat
                    ? `${BLOCKOUT_REPEAT_LABELS[repeat]}. Nobody will ask you to serve then.`
                    : 'Nobody will ask you to serve then.',
                })
                setFrom('')
                setTo('')
                setWhy('')
                setRepeat('')
                setUntil('')
                onClose()
              },
            },
          )
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <Field label="From">
            <Input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              required
            />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
        </div>
        <Field label="Repeats">
          <Select
            value={repeat}
            onChange={(event) => setRepeat(event.target.value as BlockoutRepeat | '')}
          >
            <option value="">Doesn’t repeat</option>
            {Object.entries(BLOCKOUT_REPEAT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        {repeat && (
          <Field label="Until (optional)" description="Empty: until you remove it.">
            <Input type="date" value={until} onChange={(event) => setUntil(event.target.value)} />
          </Field>
        )}
        <Field label="Why (optional)">
          <Input value={why} onChange={(event) => setWhy(event.target.value)} />
        </Field>
        <ErrorNote error={mine.addBlockout.error} />
      </form>
    </SidePanel>
  )
}
